import type { TFunction } from 'i18next'
import { parsePhoneNumberFromString } from 'libphonenumber-js/min'
import { useEffect, useRef, useState } from 'react'
import {
  Animated,
  Keyboard,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { FiatErrorNotice } from './FiatErrorNotice'
import { KycVerifiedIcon } from '../icons/KycVerifiedIcon'
import { FiatSubmissionIndicator } from './FiatSubmissionIndicator'
import { useHapticFeedback } from '../haptics/HapticsContext'
import { FiatActionButton } from './FiatActionButton'
import { useKeyboardFollow } from '../flow/useKeyboardHeight'
import { useFlowStepFrame } from '../hooks/useNestedSheetHeight'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import { FiatBackButton } from './FiatBackButton'
import { KYC_FORM_METRICS } from './kycFormTheme'
import { useOtpAutoSubmit } from './useOtpAutoSubmit'

/** What the design draws, and what a step omitting `length` falls back to. */
export const OTP_CODE_LENGTH = 6

export interface FiatOtpScreenProps {
  /** Digits the step expects, as the transition declares them. */
  verified?: boolean
  length?: number
  /**
   * Where the code went, when the client is the one that supplied it. A round
   * on a channel the entry never collected for has no value to name.
   */
  destination?: string
  /** Which channel carried it, as the step reports it. */
  isSms: boolean
  /** The step's own error — a bad code re-enters this same state. */
  errorMessage?: string
  /**
   * Sends a fresh code. The row is always drawn — every frame has it — but it
   * is live only when the round carries a transition for it; absent, the link
   * is disabled rather than a press that does nothing.
   */
  onResend?: () => void
  isSubmitting: boolean
  onChangeCode: (code: string) => void
  onSubmit: () => void
  onClose: () => void
}

/**
 * The one-time code (screen 2).
 *
 * The per-digit boxes are a display: one field holds the code, so iOS fills it
 * from the message and a paste lands whole.
 */
export function FiatOtpScreen(props: FiatOtpScreenProps) {
  const [code, setCode] = useState('')
  const wasSubmitting = useRef(false)
  useEffect(() => {
    // A repeated rejection carries the same message, so the attempt settling
    // marks the code spent rather than the text of the refusal. The edge is
    // read from a ref because the caller's handler is a fresh identity each
    // render, which as an effect dependency would clear the code forever.
    const settled = wasSubmitting.current && !props.isSubmitting
    wasSubmitting.current = props.isSubmitting
    if (settled && props.errorMessage !== undefined) {
      setCode('')
      props.onChangeCode('')
    }
  })
  return (
    <FiatOtpContent
      {...props}
      code={code}
      onChangeCode={(next) => {
        setCode(next)
        props.onChangeCode(next)
      }}
    />
  )
}

interface FiatOtpContentProps extends FiatOtpScreenProps {
  code: string
}

export function FiatOtpContent({
  verified = false,
  length = OTP_CODE_LENGTH,
  destination,
  isSms,
  errorMessage,
  onResend,
  isSubmitting,
  onChangeCode,
  onSubmit,
  onClose,
  code,
}: FiatOtpContentProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const { height, paddingTop } = useFlowStepFrame()
  const insets = useSafeAreaInsets()
  const keyboard = useKeyboardFollow(true)
  const locked = isSubmitting || verified
  const keyboardHeight = keyboard.height
  useEffect(() => {
    if (locked) {
      Keyboard.dismiss()
    }
  }, [locked])
  const field = useRef<TextInput>(null)

  const isComplete = code.length > 0 && code.length === length
  const canResend = onResend !== undefined && !locked
  const haptic = useHapticFeedback()
  useOtpAutoSubmit({
    code,
    length,
    isSubmitting: locked,
    hasError: errorMessage !== undefined,
    onSubmit,
  })
  useEffect(() => {
    if (!isSubmitting && errorMessage !== undefined) {
      haptic('error')
    }
  }, [errorMessage, isSubmitting, haptic])
  const edit = (next: string) => {
    if (locked) {
      return
    }
    const digits = next.replace(/\D/g, '').slice(0, length)
    onChangeCode(digits)
  }

  return (
    <Animated.View
      style={{
        // A flow step's sheet spans the window, status bar included, so the
        // screen takes the whole window and pays the inset itself.
        height,
        paddingTop,
        // The keyboard shortens the box. A scroll inset would not: it lets the
        // action be scrolled to, while the action rests at the bottom of a
        // viewport the keyboard still covers.
        paddingBottom: keyboard.animated,
        backgroundColor: theme.background.modal,
      }}
    >
      <FiatBackButton onPress={onClose} />

      {/* The box above is what gives up the keyboard's space; this only
          scrolls what no longer fits. */}
      <ScrollView
        style={styles.scroll}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={[
          styles.content,
          {
            // The keyboard already covers the home indicator when it is up.
            paddingBottom:
              keyboardHeight > 0
                ? KYC_FORM_METRICS.advanceGap
                : Math.max(0, insets.bottom - 7),
          },
        ]}
        // The action sits at the foot of the viewport, so it needs one to
        // sink to even when the content is shorter than the screen.
        showsVerticalScrollIndicator={false}
      >
        <Box style={styles.heading}>
          <Text weight="medium" style={styles.headingDetail}>
            {detail(t, isSms, destination)}
          </Text>
        </Box>

        <Pressable
          onPress={() => {
            if (!locked) {
              field.current?.focus()
            }
          }}
          disabled={locked}
          accessibilityState={{ disabled: locked }}
          accessibilityRole="button"
          accessibilityLabel={t('fiat.otpTitle')}
        >
          <View style={styles.codeRow}>
            {Array.from({ length }, (_, index) => (
              <CodeBox
                key={index}
                digit={code[index]}
                // The design fills the box the next digit lands in, so the
                // caret reads without one.
                isNext={index === code.length}
              />
            ))}
          </View>
        </Pressable>

        {/* - Off-screen layout preserves focus for native one-time-code fill.
            - A return-key request adds a redundant iOS button above the pad. */}
        <TextInput
          ref={field}
          value={code}
          editable={!locked}
          onChangeText={edit}
          autoFocus={!locked}
          keyboardType="number-pad"
          textContentType="oneTimeCode"
          autoComplete="one-time-code"
          maxLength={length}
          style={styles.hiddenField}
        />

        {!locked ? (
          <Pressable
            onPress={canResend ? onResend : undefined}
            disabled={!canResend}
            accessibilityRole="button"
            accessibilityLabel={t('fiat.otpResend')}
            accessibilityState={{ disabled: !canResend }}
          >
            <Text
              variant="body"
              style={[
                styles.centered,
                { color: theme.content.elements.secondary },
              ]}
            >
              {t('fiat.otpResendPrompt')}{' '}
              <Text
                style={{
                  color: canResend
                    ? theme.content.elements.hyperlink
                    : theme.content.elements.tertiary,
                }}
              >
                {t('fiat.otpResend')}
              </Text>
            </Text>
          </Pressable>
        ) : null}

        <Box style={styles.action}>
          {errorMessage !== undefined && !locked ? (
            <FiatErrorNotice message={errorMessage} />
          ) : null}
          <OtpAction
            verified={verified}
            verifiedLabel={t('fiat.otpVerified')}
            isSubmitting={isSubmitting}
            canRetry={errorMessage !== undefined && isComplete}
            label={t('common.tryAgain')}
            onPress={onSubmit}
          />
        </Box>
      </ScrollView>
    </Animated.View>
  )
}

function OtpAction({
  verified,
  verifiedLabel,
  isSubmitting,
  canRetry,
  label,
  onPress,
}: {
  verified: boolean
  verifiedLabel: string
  isSubmitting: boolean
  canRetry: boolean
  label: string
  onPress: () => void
}) {
  if (verified) {
    return (
      <Box
        accessibilityRole="image"
        accessibilityLabel={verifiedLabel}
        style={{ height: 56, alignItems: 'center', justifyContent: 'center' }}
      >
        <KycVerifiedIcon />
      </Box>
    )
  }
  if (isSubmitting) {
    return <FiatSubmissionIndicator />
  }
  if (!canRetry) {
    return null
  }
  return <FiatActionButton label={label} onPress={onPress} />
}

/**
 * Names the destination when the client knows it, and only the channel when it
 * does not — one literal key per branch, as the i18n rule requires.
 */
function detail(t: TFunction, isSms: boolean, destination?: string): string {
  if (destination === undefined) {
    return isSms ? t('fiat.otpDetailAnyPhone') : t('fiat.otpDetailAnyEmail')
  }
  return isSms
    ? t('fiat.otpDetailPhone', { phone: formatPhoneDestination(destination) })
    : t('fiat.otpDetailEmail', { email: destination })
}

/** Format a canonical destination the way people in its country read it. */
function formatPhoneDestination(destination: string): string {
  const phone = parsePhoneNumberFromString(destination)
  if (phone?.isValid() !== true) {
    return destination
  }
  return phone.formatNational()
}

function CodeBox({ digit, isNext }: { digit?: string; isNext: boolean }) {
  const theme = useTheme()

  return (
    <Box
      style={[
        styles.codeBox,
        { backgroundColor: isNext ? theme.form.fieldNext : theme.form.field },
      ]}
    >
      <Text style={[styles.digit, { color: theme.content.elements.primary }]}>
        {digit ?? ''}
      </Text>
    </Box>
  )
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  content: {
    flexGrow: 1,
    paddingTop: 52,
    paddingHorizontal: KYC_FORM_METRICS.screenInset,
    gap: 24,
  },
  heading: {
    paddingHorizontal: 6,
    gap: KYC_FORM_METRICS.headingGap,
  },
  headingDetail: {
    fontSize: KYC_FORM_METRICS.otpHeadingFontSize,
    lineHeight: KYC_FORM_METRICS.otpHeadingLineHeight,
    letterSpacing: KYC_FORM_METRICS.otpHeadingTracking,
  },
  codeRow: { flexDirection: 'row', gap: KYC_FORM_METRICS.codeBoxGap },
  codeBox: {
    flex: 1,
    height: KYC_FORM_METRICS.codeBoxHeight,
    borderRadius: KYC_FORM_METRICS.fieldRadius,
    alignItems: 'center',
    justifyContent: 'center',
  },
  digit: {
    fontSize: KYC_FORM_METRICS.codeDigitFontSize,
    lineHeight: Math.round(KYC_FORM_METRICS.codeDigitFontSize * 1.12),
  },
  hiddenField: { position: 'absolute', opacity: 0, width: 1, height: 1 },
  centered: {
    paddingHorizontal: KYC_FORM_METRICS.fieldPaddingHorizontal,
    textAlign: 'left',
  },
  // The design rests the action at the foot of the screen, riding up on the
  // keyboard rather than following the boxes.
  action: { marginTop: 'auto' },
})
